import { uniq } from 'lodash';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { DocumentUploader } from '@/modules/documents/DocumentUploader/DocumentUploader';
import { DocumentRepresentationList } from '@/modules/files/DocumentRepresentation/DocumentRepresentationList';
import { DocumentType } from '@/types/schema';

import { DSUEFormShape, DSUEFormShapePaths } from './DSUEModal.constants';
import { DsueFormClientFragment } from './graphql/DSUEForm.generated';

interface DSUEModalFormProps {
  clientData: DsueFormClientFragment[];
  householdId: string;
}

export function DSUEModalForm({ clientData, householdId }: DSUEModalFormProps) {
  const { control, setValue } = useFormContext<DSUEFormShape>();

  const atLeastOneClientHasDsue = clientData.some((client) => !!client.dsue);

  const dsueId = useWatch({
    control,
    name: 'id' as const satisfies DSUEFormShapePaths,
  });

  const documents = useWatch({
    control,
    name: 'documents' as const satisfies DSUEFormShapePaths,
  });

  const addDocumentId = useCallback(
    (documentId: string) => {
      setValue(
        'documents' as const satisfies DSUEFormShapePaths,
        uniq([...documents, documentId]),
        {
          shouldDirty: true,
          shouldValidate: true,
        }
      );
    },
    [setValue, documents]
  );

  return (
    <>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareSelectInput<DSUEFormShape>
            control={control}
            fieldName={'primaryClientId' as const satisfies DSUEFormShapePaths}
            label="Recipient"
            options={clientData.map((client) => ({
              value: client.id,
              display: client.displayName,
            }))}
            // disable the recipient select if the DSUE is already associated with a client
            disabled={!!atLeastOneClientHasDsue}
            required
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={8}>
          <FormAwareCurrencyInput<DSUEFormShape>
            control={control}
            fieldName={'amount' as const satisfies DSUEFormShapePaths}
            label="DSUE amount"
            required
            isDecimalJSInput
          />
        </FormLayoutItem>
        <FormLayoutItem width={4}>
          <FormAwareDatePickerInput
            control={control}
            fieldName={'asOfDate' as const satisfies DSUEFormShapePaths}
            label="As of date"
            required
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<DSUEFormShape>
            control={control}
            fieldName={'notes' as const satisfies DSUEFormShapePaths}
            label="Notes"
            resizable
            rows={3}
            multiline
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <DocumentUploader
            householdId={householdId}
            getCreateDocumentInput={({ fileId, fileName }) => ({
              householdID: householdId,
              type: DocumentType.Other,
              fileID: fileId,
              name: fileName,
              dsueIDs: dsueId ? [dsueId] : [],
            })}
            onDocumentUploadSuccess={({ documentId }) => {
              addDocumentId(documentId);
            }}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <DocumentRepresentationList showDelete documentIds={documents} />
        </FormLayoutItem>
      </FormLayoutRow>
    </>
  );
}
